import React, { useState, useEffect } from 'react';
import { Lock, FileText, Plus } from 'lucide-react';

const InternalNotes = ({ clientId, backendUrl, adminKey, onUpdate }) => {
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchNotes = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/clients/${clientId}`, {
        headers: {
          'x-admin-key': adminKey
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.internal_notes || []);
      }
    } catch (err) {
      console.error('Error fetching internal notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchNotes();
    }
  }, [clientId]);

  const handleAddNote = async () => {
    const message = noteText.trim();
    
    if (!message) {
      alert('Please enter a note');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/clients/${clientId}/internal-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ 
          note: message,
          author: 'Staff'
        })
      });

      if (response.ok) {
        setNoteText('');
        await fetchNotes();
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        alert(`Failed to add note: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #E2E8F0'
    }}>
      <h3 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '16px', 
        fontWeight: '700', 
        color: '#1E293B',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Lock size={20} color="#DC2626" />
        Internal Notes (Staff Only)
      </h3>

      <div style={{ 
        marginBottom: '12px', 
        padding: '10px', 
        background: '#FEF2F2', 
        borderLeft: '3px solid #DC2626',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#991B1B',
        fontWeight: '500'
      }}>
        🔒 These notes are private and NOT visible to clients
      </div>

      {/* Add Note Form */}
      <div style={{ marginBottom: '20px', padding: '16px', background: '#F8FAFC', borderRadius: '8px' }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#475569', 
            marginBottom: '6px' 
          }}>
            Internal Note
          </label>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter internal staff notes (not visible to client)..."
            rows="3"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #E2E8F0',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        <button
          onClick={handleAddNote}
          disabled={submitting || !noteText.trim()}
          style={{
            width: '100%',
            padding: '10px',
            background: submitting || !noteText.trim() ? '#CBD5E1' : '#DC2626',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: submitting || !noteText.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background 0.2s'
          }}
          onMouseOver={(e) => {
            if (noteText.trim() && !submitting) {
              e.target.style.background = '#B91C1C';
            }
          }}
          onMouseOut={(e) => {
            if (noteText.trim() && !submitting) {
              e.target.style.background = '#DC2626';
            }
          }}
        >
          <Plus size={16} />
          {submitting ? 'Adding...' : 'Add Internal Note'}
        </button>
      </div>

      {/* Notes List */}
      <div>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#64748B', marginBottom: '12px' }}>
          Note History ({notes.length})
        </h4>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94A3B8' }}>
            Loading notes...
          </div>
        ) : notes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94A3B8', fontSize: '14px' }}>
            No internal notes yet. Add one above!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {notes.map((note) => (
              <div
                key={note.id}
                style={{
                  padding: '12px',
                  background: '#FEF2F2',
                  border: '1px solid #FEE2E2',
                  borderRadius: '8px'
                }}
              >
                <div style={{ fontSize: '14px', color: '#1E293B', marginBottom: '6px' }}>
                  {note.note}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#64748B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FileText size={12} />
                  {formatDateTime(note.created_at)}
                  <span style={{ 
                    padding: '2px 6px', 
                    background: '#DC2626', 
                    color: '#fff', 
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    {note.author || 'Staff'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalNotes;
